import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Camera, Wifi, WifiOff, Clock, Trash2, Edit, CheckCircle, XCircle, Eye,
  RefreshCw, ChevronLeft, ChevronRight, Radio, Play, Square,
  Search, X, AlertCircle, MapPin, Smartphone, ArrowUpDown, ArrowUp, ArrowDown,
  HardDrive, AlertTriangle, Link2 as LinkIcon
} from 'lucide-react';
import {
  getDevices, getPendingDevices, triggerCapture, deleteDevice,
  approvePendingDevice, deletePendingDevice, updateDevice, getLatestSnapshotBase64
} from '../services/api';
import api from '../services/api';
import BarraDatos from '../components/BarraDatos';

// Compara dos respuestas de la API para decidir si merece la pena repintar.
// Es una comparacion por contenido a proposito: la identidad del array cambia
// en cada poll aunque los datos sean identicos.
const sonIguales = (a, b) => {
  try { return JSON.stringify(a) === JSON.stringify(b); } catch (e) { return false; }
};

const Dashboard = () => {
  const [devices, setDevices]               = useState([]);
  const [mdvrStatus, setMdvrStatus]         = useState([]);
  const [health, setHealth]                 = useState({});   // {device_id: {...}}
  const [pendingDevices, setPendingDevices] = useState([]);
  const [activeTab, setActiveTab]           = useState('devices');
  const [loading, setLoading]               = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showModal, setShowModal]           = useState(false);
  const [modalType, setModalType]           = useState('');
  const [snapshotPreview, setSnapshotPreview] = useState(null);
  const [monitorDevice, setMonitorDevice]   = useState(null);

  // Filtros / Sort
  const [search, setSearch]         = useState('');
  const [filterConn, setFilterConn] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortKey, setSortKey]       = useState('name');
  const [sortDir, setSortDir]       = useState('asc');

  // Hora + SD por dispositivo (consulta ISAPI en vivo, solo online)
  const [quick, setQuick] = useState({});   // { device_id: {loading,error,time,sd} }

  const loadData = useCallback(async () => {
    if (showModal || snapshotPreview || monitorDevice) return;
    try {
      const [devRes, pendRes, mdvrRes] = await Promise.all([
        getDevices(),
        getPendingDevices(),
        api.get('/api/mdvr/status').catch(() => ({ data: [] })),
      ]);
      // Solo se actualiza el estado si algo CAMBIO de verdad. Antes se
      // reemplazaba siempre, y como React ve un array nuevo, la tabla se
      // repintaba cada 30 s aunque no hubiera pasado nada: parpadeo constante
      // y, peor, reinicio del temporizador de la consulta de hora/SD.
      setDevices(prev => sonIguales(prev, devRes.data) ? prev : devRes.data);
      setPendingDevices(prev => sonIguales(prev, pendRes.data) ? prev : pendRes.data);
      setMdvrStatus(prev => sonIguales(prev, mdvrRes.data || []) ? prev : (mdvrRes.data || []));
      // Salud POR SEPARADO: puede tardar (contención con el chequeo) y NO debe
      // bloquear la carga del panel. La columna Cámaras se llena cuando llegue.
      api.get('/api/health/dashboard').then(r => {
        const hmap = {};
        (r.data?.devices || []).forEach(h => { hmap[h.device_id] = h; });
        setHealth(hmap);
      }).catch(() => {});
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [showModal, snapshotPreview, monitorDevice]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Consulta hora + SD de los dispositivos EN LÍNEA. Refresca cada 60s.
  //
  // Depende de los IDS en línea, no del array `devices`: con el array, cada
  // poll de 30 s creaba una referencia nueva, el efecto se rearmaba y el
  // temporizador de 60 s no llegaba a cumplirse nunca -- las consultas salían
  // de hecho cada 30 s, el doble de lo previsto.
  const idsEnLinea = useMemo(
    () => devices.filter(d => d.is_online).map(d => d.device_id).sort().join(','),
    [devices]
  );

  useEffect(() => {
    const ids = idsEnLinea ? idsEnLinea.split(',') : [];
    if (!ids.length) return undefined;
    let alive = true;
    const fetchQuick = () => {
      ids.forEach(id => {
        // Solo se marca "cargando" la PRIMERA vez. En los refrescos se deja el
        // valor anterior a la vista: si no, cada minuto la celda parpadeaba a
        // "…" y volvía, que era el movimiento más molesto de la tabla.
        setQuick(q => (q[id] ? q : { ...q, [id]: { loading: true } }));
        api.get(`/api/devices/${id}/quickcheck`)
          .then(r => { if (alive) setQuick(q => ({ ...q, [id]: { loading: false, ...r.data } })); })
          .catch(() => { if (alive) setQuick(q => ({ ...q, [id]: { ...(q[id] || {}), loading: false, error: true } })); });
      });
    };
    fetchQuick();
    const id = setInterval(fetchQuick, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [idsEnLinea]);

  // ============ Consumo de datos que reporta Samsara ============
  // Una sola llamada; el backend responde desde la BD (nunca habla con Samsara
  // dentro de la peticion). Si no esta configurado, devuelve vacio y la columna
  // se queda en '—' sin estorbar.
  const [datosSamsara, setDatosSamsara] = useState({ plan: 0, porEquipo: {} });
  useEffect(() => {
    let vivo = true;
    const cargar = () => api.get('/api/samsara/data-usage')
      .then(r => {
        if (!vivo) return;
        const porEquipo = {};
        (r.data?.unidades || []).forEach(u => { porEquipo[u.device_id] = u; });
        setDatosSamsara({ plan: r.data?.plan_bytes || 0, porEquipo });
      })
      .catch(() => {});
    cargar();
    const t = setInterval(cargar, 300000);   // cambia cada hora; 5 min sobra
    return () => { vivo = false; clearInterval(t); };
  }, []);

  // ============ Merge devices + mdvr status ============
  const enrichedDevices = useMemo(() => {
    const mdvrMap = mdvrStatus.reduce((m, x) => { m[x.device_id] = x; return m; }, {});
    return devices.map(d => {
      const mdvr = mdvrMap[d.device_id] || {};
      const h = health[d.device_id] || {};
      return {
        ...d,
        connection_type:    mdvr.connection_type || 'desconocido',
        sucursal:           mdvr.sucursal || null,
        ip_address:         mdvr.ip_address || null,
        alertas_abiertas:   mdvr.alertas_abiertas || 0,
        horas_desde_conexion: mdvr.horas_desde_conexion,
        connected_at:       mdvr.connected_at || null,
        disconnected_at:    mdvr.disconnected_at || null,
        is_mdvr:            !!mdvrMap[d.device_id],
        channels_with_signal: h.channels_with_signal,
        channels_total:       h.channels_total,
        channels_expected:    h.channels_expected,
        health_summary:       h.issues_summary || null,
      };
    });
  }, [devices, mdvrStatus, health]);

  // ============ Filtrado + Sort ============
  const filteredDevices = useMemo(() => {
    let data = [...enrichedDevices];

    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(d =>
        (d.name || '').toLowerCase().includes(q) ||
        (d.device_id || '').toLowerCase().includes(q) ||
        (d.sucursal || '').toLowerCase().includes(q) ||
        (d.ip_address || '').includes(q)
      );
    }
    if (filterConn !== 'all') {
      data = data.filter(d => (d.connection_type || 'desconocido') === filterConn);
    }
    if (filterStatus === 'online')   data = data.filter(d => d.is_online);
    if (filterStatus === 'offline')  data = data.filter(d => !d.is_online);
    if (filterStatus === 'alertas')  data = data.filter(d => d.alertas_abiertas > 0);

    data.sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = (typeof va === 'number' && typeof vb === 'number')
        ? va - vb
        : String(va).localeCompare(String(vb), 'es', { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return data;
  }, [enrichedDevices, search, filterConn, filterStatus, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  // ============ KPIs ============
  const kpis = useMemo(() => ({
    total:    devices.length,
    online:   devices.filter(d => d.is_online).length,
    pending:  pendingDevices.length,
    samsara:  enrichedDevices.filter(d => d.connection_type === 'samsara').length,
    wifi:     enrichedDevices.filter(d => d.connection_type === 'wifi_sucursal').length,
    cellular: enrichedDevices.filter(d => d.connection_type === 'datos_celular').length,
    alertas:  enrichedDevices.filter(d => d.alertas_abiertas > 0).length,
  }), [devices, pendingDevices, enrichedDevices]);

  // ============ Handlers ============
  // --- Ligar equipo a su unidad (economico) ---
  // Un equipo sin ligar no tiene economico, y eso se paga en tres lados: el portal
  // externo lo entrega sin unitNumber, Grafana lo cuenta como no configurado, y el
  // consumo de Samsara no cruza con el (el cruce es por economico), asi que su
  // barra sale vacia.
  const [ligando, setLigando] = useState(null);      // device al que se le esta ligando
  const [unidades, setUnidades] = useState(null);    // null = cargando
  const [ligandoError, setLigandoError] = useState(null);

  const abrirLigar = async (device) => {
    setLigando(device); setUnidades(null); setLigandoError(null);
    try {
      const r = await api.get('/api/dbpaq/mdvr-disponibles');
      setUnidades(r.data || []);
    } catch (err) {
      setUnidades([]);
      setLigandoError(err.response?.data?.detail || 'No se pudo consultar DB_PAQ');
    }
  };

  const confirmarLigar = async (unidadId) => {
    try {
      const r = await api.post(`/api/dbpaq/sync-device?device_id=${encodeURIComponent(ligando.device_id)}`
                               + `&numero_unidad_id=${unidadId}`);
      setLigando(null);
      loadData();
      alert(`Equipo ligado a la unidad ${r.data?.numero_unidad || ''}. `
            + 'La barra de datos aparece en la siguiente sincronizacion con Samsara (cada hora).');
    } catch (err) {
      setLigandoError(err.response?.data?.detail || 'No se pudo ligar');
    }
  };

  const handleCapture = async (deviceId) => {
    try {
      await triggerCapture(deviceId);
      alert('Captura iniciada');
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || 'Error desconocido'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este dispositivo?')) return;
    try {
      await deleteDevice(id);
      loadData();
    } catch (err) {
      alert('Error eliminando dispositivo');
    }
  };

  const handleApprove = (device) => { setSelectedDevice(device); setModalType('approve'); setShowModal(true); };
  const handleEdit = (device) => { setSelectedDevice(device); setModalType('edit'); setShowModal(true); };
  const handleViewSnapshot = (device) => setSnapshotPreview({ device, channel: device.default_channel || 1 });
  const handleMonitor = (device) => setMonitorDevice({ device, channel: device.default_channel || 1 });

  const handleIgnorePending = async (id) => {
    if (!window.confirm('¿Ignorar este dispositivo?')) return;
    try { await deletePendingDevice(id); loadData(); }
    catch (err) { alert('Error'); }
  };

  const refreshQuickOne = (deviceId) => {
    api.get(`/api/devices/${deviceId}/quickcheck`)
      .then(r => setQuick(q => ({ ...q, [deviceId]: { loading: false, ...r.data } })))
      .catch(() => {});
  };

  const handleSyncTime = async (deviceId) => {
    if (!window.confirm('¿Poner la hora del equipo igual a la del servidor?')) return;
    setQuick(q => ({ ...q, [deviceId]: { ...(q[deviceId] || {}), busy: 'time' } }));
    try {
      await api.post(`/api/devices/${deviceId}/sync-time`);
      refreshQuickOne(deviceId);
    } catch (err) {
      alert('No se pudo poner la hora: ' + (err.response?.data?.detail || 'error'));
      setQuick(q => ({ ...q, [deviceId]: { ...(q[deviceId] || {}), busy: null } }));
    }
  };

  const handleFormatSd = async (deviceId) => {
    if (!window.confirm(
      'FORMATEAR la SD borra TODAS las grabaciones del equipo.\n\n' +
      '¿Continuar con el formateo de ' + deviceId + '?'
    )) return;
    setQuick(q => ({ ...q, [deviceId]: { ...(q[deviceId] || {}), busy: 'format', fmtPct: 0 } }));
    try {
      await api.post(`/api/devices/${deviceId}/format-sd`);
      // Poll de progreso hasta que termine. Dos plazos, porque el equipo no
      // avisa de inmediato: se le dan 20 s para que reporte que empezó (si no,
      // se deja de esperar) y un tope de 10 min para una SD grande.
      let empezo = false;
      const arranque = Date.now() + 20000;
      const tope = Date.now() + 10 * 60 * 1000;
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/api/devices/${deviceId}/format-status`);
          if (r.data.formating) empezo = true;
          setQuick(q => ({ ...q, [deviceId]: { ...(q[deviceId] || {}), busy: 'format', fmtPct: r.data.percent } }));
          const acabo = empezo ? !r.data.formating : Date.now() > arranque;
          if (acabo || Date.now() > tope) {
            clearInterval(poll);
            refreshQuickOne(deviceId);
          }
        } catch (e) { clearInterval(poll); refreshQuickOne(deviceId); }
      }, 2000);
    } catch (err) {
      alert('No se pudo formatear: ' + (err.response?.data?.detail || 'error'));
      setQuick(q => ({ ...q, [deviceId]: { ...(q[deviceId] || {}), busy: null } }));
    }
  };

  return (
    <div style={S.container}>
      {/* KPIs */}
      <div style={S.kpiGrid}>
        <Kpi icon={<Camera size={22}/>}     label="Dispositivos" value={kpis.total}    color="#3b82f6"/>
        <Kpi icon={<Wifi size={22}/>}       label="En línea"     value={kpis.online}   color="#10b981"/>
        <Kpi icon={<Clock size={22}/>}      label="Pendientes"   value={kpis.pending}  color="#f59e0b"
             onClick={() => setActiveTab('pending')}/>
        <Kpi icon={<MapPin size={22}/>}     label="WiFi sucursal" value={kpis.wifi}    color="#06b6d4"
             onClick={() => { setActiveTab('devices'); setFilterConn('wifi_sucursal'); }}/>
        <Kpi icon={<Smartphone size={22}/>} label="Samsara"      value={kpis.samsara}  color="#8b5cf6"
             onClick={() => { setActiveTab('devices'); setFilterConn('samsara'); }}/>
        <Kpi icon={<Smartphone size={22}/>} label="Datos/SIM"    value={kpis.cellular} color="#f59e0b"
             onClick={() => { setActiveTab('devices'); setFilterConn('datos_celular'); }}/>
        <Kpi icon={<AlertCircle size={22}/>} label="Con alertas" value={kpis.alertas}  color="#ef4444"
             onClick={() => { setActiveTab('devices'); setFilterStatus('alertas'); }}/>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        <button style={activeTab === 'devices' ? S.tabActive : S.tab}
                onClick={() => setActiveTab('devices')}>
          Dispositivos ({filteredDevices.length}/{devices.length})
        </button>
        <button style={activeTab === 'pending' ? S.tabActive : S.tab}
                onClick={() => setActiveTab('pending')}>
          Nuevos ({pendingDevices.length})
        </button>
      </div>

      {/* Filtros (solo en tab devices) */}
      {activeTab === 'devices' && (
        <div style={S.filterBar}>
          <div style={S.searchBox}>
            <Search size={16}/>
            <input placeholder="Buscar nombre, device, sucursal, IP..."
                   value={search} onChange={e => setSearch(e.target.value)}
                   style={S.searchInput}/>
            {search && <X size={16} style={{ cursor: 'pointer' }} onClick={() => setSearch('')}/>}
          </div>
          <select value={filterConn} onChange={e => setFilterConn(e.target.value)} style={S.select}>
            <option value="all">Toda conexión</option>
            <option value="wifi_sucursal">WiFi sucursal</option>
            <option value="samsara">Samsara</option>
            <option value="datos_celular">Datos/SIM</option>
            <option value="desconocido">Desconocido</option>
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={S.select}>
            <option value="all">Todo estado</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="alertas">Con alertas</option>
          </select>
          {(filterConn !== 'all' || filterStatus !== 'all' || search) && (
            <button style={S.clearBtn} onClick={() => {
              setFilterConn('all'); setFilterStatus('all'); setSearch('');
            }}>
              <X size={14}/> Limpiar
            </button>
          )}
        </div>
      )}

      {loading ? <div style={S.loading}>Cargando...</div> :
        activeTab === 'devices'
          ? <DevicesTable devices={filteredDevices} quick={quick} datosSamsara={datosSamsara}
                          sortKey={sortKey} sortDir={sortDir} toggleSort={toggleSort}
                          onCapture={handleCapture} onDelete={handleDelete}
                          onEdit={handleEdit} onViewSnapshot={handleViewSnapshot}
                          onMonitor={handleMonitor}
                          onSyncTime={handleSyncTime} onFormatSd={handleFormatSd}
                          onLigar={abrirLigar}/>
          : <PendingTable devices={pendingDevices}
                          onApprove={handleApprove} onIgnore={handleIgnorePending}/>
      }

      {ligando && <LigarModal device={ligando} unidades={unidades} error={ligandoError}
                              onClose={() => setLigando(null)} onConfirm={confirmarLigar}/>}

      {showModal && <Modal type={modalType} device={selectedDevice}
                           onClose={() => setShowModal(false)} onSave={loadData}/>}
      {snapshotPreview && <SnapshotModal data={snapshotPreview}
                                          onClose={() => setSnapshotPreview(null)}/>}
      {monitorDevice && <MonitorModal data={monitorDevice}
                                       onClose={() => setMonitorDevice(null)}/>}
    </div>
  );
};

// ============ Componentes auxiliares ============
const Kpi = ({ icon, label, value, color, onClick }) => (
  <div style={{ ...S.kpiCard, cursor: onClick ? 'pointer' : 'default', borderLeftColor: color }}
       onClick={onClick}>
    <div style={{ ...S.kpiIcon, background: `${color}20`, color }}>{icon}</div>
    <div>
      <div style={S.kpiValue}>{value}</div>
      <div style={S.kpiLabel}>{label}</div>
    </div>
  </div>
);

const SortIcon = ({ k, sortKey, sortDir }) => {
  if (sortKey !== k) return <ArrowUpDown size={12} style={{ opacity: 0.4 }}/>;
  return sortDir === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>;
};

const Th = ({ k, children, sortKey, sortDir, toggleSort }) => (
  <th style={S.th} onClick={() => toggleSort(k)}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {children} <SortIcon k={k} sortKey={sortKey} sortDir={sortDir}/>
    </div>
  </th>
);

const StatusBadge = ({ online }) => (
  online
    ? <span style={S.online}><Wifi size={14}/> Online</span>
    : <span style={S.offline}><WifiOff size={14}/> Offline</span>
);

const ConnTypeBadge = ({ type }) => {
  const cfg = {
    wifi_sucursal:  { bg: '#cffafe', fg: '#0e7490', label: 'WiFi' },
    samsara:        { bg: '#ede9fe', fg: '#5b21b6', label: 'Samsara' },
    datos_celular:  { bg: '#fef3c7', fg: '#92400e', label: 'Datos' },
    desconocido:    { bg: '#f3f4f6', fg: '#6b7280', label: '—' },
  }[type || 'desconocido'];
  return <span style={{ ...S.badge, background: cfg.bg, color: cfg.fg }}>{cfg.label}</span>;
};

// Desfase legible (segundos → s/min/h/días)
const fmtDrift = (sec) => {
  if (sec == null) return null;
  const a = Math.abs(sec);
  if (a < 60)    return `${sec}s`;
  if (a < 3600)  return `${Math.round(sec / 60)} min`;
  if (a < 86400) return `${Math.round(sec / 3600)} h`;
  return `${Math.round(sec / 86400)} d`;
};

const CamerasCell = ({ online, device }) => {
  if (!online) return <span style={{ color: '#9ca3af' }}>—</span>;
  const ws = device.channels_with_signal;
  const tot = device.channels_expected ?? device.channels_total;
  if (ws == null || tot == null) return <span style={{ color: '#9ca3af', fontSize: 12 }}>…</span>;
  const missing = tot - ws;
  // Tres casos, no dos. Que el equipo reporte MAS canales con señal de los que
  // el snap tiene configurados no es "todo bien": significa que la lista de
  // canales activos quedó corta, y de ahí salen canales que nunca se capturan.
  const sobran = -missing;
  const ok = missing === 0;
  const titulo = ok ? 'Todas las cámaras con señal'
    : sobran > 0
      ? `El equipo reporta ${ws} canales con señal y el snap tiene ${tot} configurados. `
        + 'Revisa los canales activos del equipo.'
      : (device.health_summary || `${missing} cámara(s) sin señal`);
  return (
    <span
      title={titulo}
      style={{
        ...S.badge, display: 'inline-flex', alignItems: 'center', gap: 4,
        background: ok ? '#dcfce7' : '#fef3c7',
        color: ok ? '#166534' : '#92400e',
      }}>
      <Camera size={11}/> {ws}/{tot}{ok ? '' : (sobran > 0 ? ` · +${sobran}` : ` · −${missing}`)}
    </span>
  );
};

// Cuándo se conectó (si está en línea) o cuándo se le vio por última vez.
// El dato ya venía en /api/mdvr/status y no se estaba usando.
const UltimaConexionCell = ({ device }) => {
  const cuando = device.is_online ? device.connected_at
                                  : (device.disconnected_at || device.connected_at);
  if (!cuando) return <span style={{ color: '#9ca3af' }}>—</span>;
  const d = new Date(cuando);
  if (isNaN(d)) return <span style={{ color: '#9ca3af' }}>—</span>;
  const dosDig = (n) => String(n).padStart(2, '0');
  const hora = `${dosDig(d.getHours())}:${dosDig(d.getMinutes())}`;
  const fecha = `${dosDig(d.getDate())}/${dosDig(d.getMonth() + 1)}`;
  const hoy = new Date().toDateString() === d.toDateString();
  return (
    <div style={{ fontSize: 12 }} title={d.toLocaleString()}>
      <div style={{ fontFamily: 'monospace', color: device.is_online ? '#111827' : '#6b7280' }}>{hora}</div>
      <div style={{ fontSize: 10, color: '#6b7280' }}>
        {hoy ? (device.is_online ? 'desde hoy' : 'hoy') : fecha}
      </div>
    </div>
  );
};

const NetBadge = ({ online, q }) => {
  if (!online) return null;
  // El backend informa CÓMO respondió: via='lan' (RTSP/descarga limpia),
  // via='isup' (remoto por el túnel; control y búsqueda sí, video comprimido)
  // o via='n9m' (MDVR EPCOM: ni ISAPI ni túnel, todo por el servidor N9M).
  if (!q || q.loading) return null;
  if (q.via === 'n9m') {
    return (
      <span style={{ ...S.badge, background: '#e0e7ff', color: '#3730a3', marginTop: 3, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <Smartphone size={10}/> EPCOM · N9M
      </span>
    );
  }
  const lan = q.via ? q.via === 'lan' : (q.reachable === true || (!q.error && (q.time || q.sd)));
  return lan
    ? <span style={{ ...S.badge, background: '#dcfce7', color: '#166534', marginTop: 3, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <MapPin size={10}/> LAN
      </span>
    : <span style={{ ...S.badge, background: '#fef3c7', color: '#92400e', marginTop: 3, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <Smartphone size={10}/> Remoto · ISUP
      </span>;
};

// Las dos acciones de estas celdas no viajan por el mismo camino, así que se
// permiten por separado en vez de con un único "habla ISAPI":
//
//   Sincronizar hora -> ISAPI en Hikvision, DEVEMM/SETCTRLUTC en los EPCOM.
//                       Ambos saben hacerlo, así que va siempre.
//   Formatear SD     -> solo ISAPI. En N9M no existe equivalente, y un botón
//                       muerto solo confunde.
const isN9M = (d) => String(d?.protocol_type || '').toUpperCase() === 'N9M';
const puedeFormatear = (d) => !isN9M(d);

const DeviceTimeCell = ({ online, q, onSyncTime, deviceId }) => {
  if (!online) return <span style={{ color: '#9ca3af' }}>—</span>;
  if (q && q.busy === 'time') return <span style={{ color: '#2563eb', fontSize: 12 }}><RefreshCw size={11} className="spin"/> ajustando…</span>;
  if (!q || q.loading) return <span style={{ color: '#9ca3af', fontSize: 12 }}>…</span>;
  const t = q.time;
  const btn = (
    <button style={S.miniBtn} title="Poner hora = servidor" onClick={() => onSyncTime(deviceId)}>
      <Clock size={11}/> Sincronizar
    </button>
  );
  // Zona distinta a la del servidor: el equipo GRABA con esa diferencia y
  // sincronizar no la corrige, porque SETCTRLUTC pone el instante UTC y no la
  // zona. Solo se arregla en el menú del equipo, así que conviene verlo de un
  // vistazo en la tabla en vez de descubrirlo equipo por equipo.
  const zonaMal = q.zona_desfase_horas != null && q.zona_desfase_horas !== 0;
  if (q.error || !t || !t.local_time)
    return <div style={{ fontSize: 12 }}><span style={{ color: '#9ca3af' }}>sin hora</span>{btn && <div style={{ marginTop: 3 }}>{btn}</div>}</div>;
  const bad = t.drift_seconds != null && Math.abs(t.drift_seconds) > 120;
  const m = t.local_time.match(/(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/);
  const shown = m ? m[2] : t.local_time;
  const date = m ? m[1] : null;
  return (
    <div style={{ fontSize: 13 }}>
      <div style={{ fontFamily: 'monospace', color: bad ? '#b45309' : '#111827' }}>{shown}</div>
      {date && <div style={{ fontSize: 10, color: '#6b7280', fontFamily: 'monospace' }}>{date}</div>}
      {bad && (
        <div style={{ fontSize: 10, color: '#b45309', display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
          <AlertTriangle size={10}/> desfase {fmtDrift(t.drift_seconds)}
        </div>
      )}
      {zonaMal && (
        <div style={{ fontSize: 10, color: '#b91c1c', fontWeight: 600, marginTop: 1 }}
             title={`El equipo tiene la zona ${q.zona} y el servidor otra distinta. Graba con esa diferencia y sincronizar NO lo corrige: hay que cambiar la zona en el menú del equipo.`}>
          zona {q.zona} · graba {q.zona_desfase_horas > 0 ? '+' : ''}{q.zona_desfase_horas} h
        </div>
      )}
      {btn && <div style={{ marginTop: 3 }}>{btn}</div>}
    </div>
  );
};

// La "IP del equipo" son dos cosas distintas y la celda mostraba una sola:
//
//   local   -> 10.x / 192.168.x, la de SU red. Es la que sirve para ir a
//              buscarlo, y en los remotos no se conocia.
//   publica -> desde donde entra al servidor. Es el NAT de la sucursal, la
//              MISMA para todos los equipos de ahi, asi que pintada a secas
//              parecia la del equipo y no lo era.
//
// La local la descubre el quickcheck preguntandosela al propio equipo por el
// tunel ISUP; hasta que llega, se muestra la ultima guardada (o solo la publica).
// Lo que el equipo contestó, tal cual, para el tooltip: un MDVR trae la ethernet
// (casi siempre sin cable, con la IP de fábrica) y la WiFi, que es la de verdad.
const detalleIps = (q) => (q?.local_ips || [])
  .map(i => `if${i.id || '?'}=${i.ip}${i.tipo ? ` (${i.tipo})` : ''}`)
  .join(' · ');

const UbicacionCell = ({ device, q }) => {
  const lan = q?.local_ip || device.local_ip || null;
  const publica = device.ip_address && device.ip_address !== lan ? device.ip_address : null;
  const reporta = detalleIps(q);
  // El equipo contestó con varias y ninguna se pudo dar por buena. Decirlo es
  // mejor que no mostrar nada: es la diferencia entre "no se sabe" y "no se
  // preguntó", y con el tooltip se ve qué contestó.
  const ambigua = !lan && !!reporta;
  if (!device.sucursal && !lan && !publica && !ambigua) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <>
      {device.sucursal && <div style={{ fontSize: 13 }}>{device.sucursal}</div>}
      <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
        {lan && (
          <div title={reporta ? `El equipo reporta: ${reporta}` : 'IP local del equipo, dentro de su red'}>
            {lan}
          </div>
        )}
        {ambigua && (
          <div style={{ color: '#b45309' }} title={`El equipo reporta: ${reporta}`}>
            IP local ambigua
          </div>
        )}
        {publica && (
          <div style={{ color: '#9ca3af' }} title="IP pública desde la que conecta (el NAT de la sucursal)">
            {publica}
          </div>
        )}
      </div>
      <NetBadge online={device.is_online} q={q}/>
    </>
  );
};

const SdCell = ({ online, q, onFormatSd, deviceId, puedeFormatear = true }) => {
  if (!online) return <span style={{ color: '#9ca3af' }}>—</span>;
  if (q && q.busy === 'format')
    return <span style={{ color: '#2563eb', fontSize: 12 }}><RefreshCw size={11} className="spin"/> formateando {q.fmtPct != null ? `${q.fmtPct}%` : '…'}</span>;
  if (!q || q.loading) return <span style={{ color: '#9ca3af', fontSize: 12 }}>…</span>;
  const sd = q.sd;
  if (q.error || !sd) return <span style={{ color: '#9ca3af', fontSize: 12 }}>sin SD</span>;

  const cfg = {
    ok:          { bg: '#dcfce7', fg: '#166534', label: 'OK' },
    idle:        { bg: '#dcfce7', fg: '#166534', label: 'OK' },
    unformatted: { bg: '#fef3c7', fg: '#92400e', label: 'Sin formato' },
    error:       { bg: '#fee2e2', fg: '#991b1b', label: 'Error' },
    unstable:    { bg: '#fee2e2', fg: '#991b1b', label: 'Inestable' },
  }[(sd.status || '').toLowerCase()] || { bg: '#f3f4f6', fg: '#374151', label: sd.status || '—' };

  const gb = (mb) => (mb || mb === 0) ? `${(mb / 1024).toFixed(0)} GB` : null;
  const needsFormat = ['unformatted', 'error', 'unstable'].includes((sd.status || '').toLowerCase());
  return (
    <div style={{ fontSize: 12 }}>
      <span style={{ ...S.badge, background: cfg.bg, color: cfg.fg, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <HardDrive size={11}/> {cfg.label}
      </span>
      {sd.capacity_mb != null && (
        <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
          {gb(sd.capacity_mb)}{sd.usage_pct != null ? ` · ${sd.usage_pct}% usado` : ''}
        </div>
      )}
      {puedeFormatear && (
        <div style={{ marginTop: 3 }}>
          <button style={needsFormat ? S.miniBtnWarn : S.miniBtn} title="Formatear la SD (borra grabaciones)"
                  onClick={() => onFormatSd(deviceId)}>
            <HardDrive size={11}/> Formatear
          </button>
        </div>
      )}
    </div>
  );
};

const DevicesTable = ({ devices, quick, datosSamsara, sortKey, sortDir, toggleSort,
                        onCapture, onDelete, onEdit, onViewSnapshot, onMonitor,
                        onSyncTime, onFormatSd, onLigar }) => (
  <div style={S.tableContainer}>
    <table style={S.table}>
      <thead>
        <tr>
          <Th k="is_online" sortKey={sortKey} sortDir={sortDir} toggleSort={toggleSort}>Estado</Th>
          <Th k="name"      sortKey={sortKey} sortDir={sortDir} toggleSort={toggleSort}>Nombre</Th>
          <Th k="device_id" sortKey={sortKey} sortDir={sortDir} toggleSort={toggleSort}>Device ID</Th>
          <Th k="protocol_type" sortKey={sortKey} sortDir={sortDir} toggleSort={toggleSort}>Proto</Th>
          <Th k="connection_type" sortKey={sortKey} sortDir={sortDir} toggleSort={toggleSort}>Conexión</Th>
          <Th k="sucursal"  sortKey={sortKey} sortDir={sortDir} toggleSort={toggleSort}>Sucursal/IP</Th>
          <Th k="connected_at" sortKey={sortKey} sortDir={sortDir} toggleSort={toggleSort}>Última conexión</Th>
          <th style={S.th}>Cámaras</th>
          <th style={S.th}>Hora equipo</th>
          <th style={S.th}>SD</th>
          <Th k="snapshot_interval" sortKey={sortKey} sortDir={sortDir} toggleSort={toggleSort}>Intervalo</Th>
          <th style={S.th} title="Hotspot Wi-Fi del gateway Samsara en el ciclo (corta el dia 1). Al 100% Samsara lo restringe a su propia app, el MDVR se queda sin internet y el snap le baja la cadencia de captura. En gris = sin lectura fresca, la cifra puede ser del ciclo anterior.">Datos</th>
          <Th k="alertas_abiertas" sortKey={sortKey} sortDir={sortDir} toggleSort={toggleSort}>Alertas</Th>
          <th style={S.th}>Acciones</th>
        </tr>
      </thead>
      <tbody>
        {devices.map(device => (
          <tr key={device.id} style={S.tr}>
            <td style={S.td}><StatusBadge online={device.is_online}/></td>
            <td style={S.td}>
              <strong>{device.name}</strong>
              {device.numero_unidad
                ? <div style={S.unidadTag}>ECO {device.numero_unidad}</div>
                : <button style={S.ligarBtn} onClick={() => onLigar(device)}
                          title="Sin unidad ligada: no cruza con el consumo de Samsara ni sale con economico en el portal">
                    <LinkIcon size={11}/> Ligar unidad
                  </button>}
            </td>
            <td style={S.td}><code style={S.code}>{device.device_id}</code></td>
            <td style={S.td}>
              <span style={{
                ...S.protocolBadge,
                background: device.protocol_type === 'JT808' ? '#8b5cf620' : '#3b82f620',
                color:      device.protocol_type === 'JT808' ? '#8b5cf6' : '#3b82f6'
              }}>
                {device.protocol_type || 'ISUP'}
              </span>
            </td>
            <td style={S.td}><ConnTypeBadge type={device.connection_type}/></td>
            <td style={S.td}><UbicacionCell device={device} q={quick[device.device_id]}/></td>
            <td style={S.td}><UltimaConexionCell device={device}/></td>
            <td style={S.td}><CamerasCell online={device.is_online} device={device}/></td>
            <td style={S.td}><DeviceTimeCell online={device.is_online} q={quick[device.device_id]}
                    deviceId={device.device_id} onSyncTime={onSyncTime}/></td>
            <td style={S.td}><SdCell online={device.is_online} q={quick[device.device_id]}
                    deviceId={device.device_id} onFormatSd={onFormatSd} puedeFormatear={puedeFormatear(device)}/></td>
            <td style={S.td}>{device.snapshot_interval}s</td>
            <td style={S.td}>
              <BarraDatos compacta
                          usados={datosSamsara?.porEquipo?.[device.device_id]?.hotspot_bytes}
                          plan={datosSamsara?.plan}
                          vieja={datosSamsara?.porEquipo?.[device.device_id]?.lectura_vieja}
                          edadMin={datosSamsara?.porEquipo?.[device.device_id]?.edad_min} />
            </td>
            <td style={S.td}>
              {device.alertas_abiertas > 0
                ? <span style={S.alertBadge}>
                    <AlertCircle size={12}/> {device.alertas_abiertas}
                  </span>
                : <span style={{ color: '#9ca3af' }}>—</span>}
            </td>
            <td style={S.td}>
              <div style={S.actions}>
                <button style={S.actionBtn} onClick={() => onViewSnapshot(device)} title="Ver snapshot">
                  <Eye size={16}/>
                </button>
                <button style={{ ...S.actionBtn, color: '#10b981' }} onClick={() => onMonitor(device)} title="Monitor en vivo">
                  <Radio size={16}/>
                </button>
                <button style={S.actionBtn} onClick={() => onCapture(device.device_id)} title="Capturar">
                  <Camera size={16}/>
                </button>
                <button style={S.actionBtn} onClick={() => onEdit(device)} title="Editar">
                  <Edit size={16}/>
                </button>
                <button style={{ ...S.actionBtn, color: '#ef4444' }} onClick={() => onDelete(device.id)} title="Eliminar">
                  <Trash2 size={16}/>
                </button>
              </div>
            </td>
          </tr>
        ))}
        {devices.length === 0 && (
          <tr><td colSpan="11" style={S.empty}>Sin dispositivos que coincidan con los filtros</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const PendingTable = ({ devices, onApprove, onIgnore }) => (
  <div style={S.tableContainer}>
    <table style={S.table}>
      <thead>
        <tr>
          <th style={S.th}>Device ID</th>
          <th style={S.th}>IP</th>
          <th style={S.th}>Primera conexión</th>
          <th style={S.th}>Última conexión</th>
          <th style={S.th}>Acciones</th>
        </tr>
      </thead>
      <tbody>
        {devices.map(device => (
          <tr key={device.id} style={S.tr}>
            <td style={S.td}><code style={S.code}>{device.device_id}</code></td>
            <td style={S.td}>{device.ip_address || '-'}</td>
            <td style={S.td}>{new Date(device.first_seen).toLocaleString()}</td>
            <td style={S.td}>{new Date(device.last_seen).toLocaleString()}</td>
            <td style={S.td}>
              <div style={S.actions}>
                <button style={{ ...S.actionBtn, color: '#10b981' }} onClick={() => onApprove(device)}>
                  <CheckCircle size={16}/> Registrar
                </button>
                <button style={{ ...S.actionBtn, color: '#ef4444' }} onClick={() => onIgnore(device.id)}>
                  <XCircle size={16}/>
                </button>
              </div>
            </td>
          </tr>
        ))}
        {devices.length === 0 && (
          <tr><td colSpan="5" style={S.empty}>No hay dispositivos pendientes</td></tr>
        )}
      </tbody>
    </table>
  </div>
);








// ╔═══════════════════════════════════════════════════════════╗
// ║ PARTE 3: Componente nuevo — pegar ANTES de "const Modal"  ║
// ╚═══════════════════════════════════════════════════════════╝
 
const ChannelPreviewSelector = ({ label, deviceId, totalChannels, value, onChange }) => {
  const [thumbnail, setThumbnail] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [capturedAt, setCapturedAt] = React.useState(null);
 
  const loadThumbnail = React.useCallback(async (ch) => {
    if (!deviceId || !ch) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getLatestSnapshotBase64(deviceId, ch);
      setThumbnail(res.data.image);
      setCapturedAt(res.data.captured_at);
    } catch (err) {
      setThumbnail(null);
      setError('Sin snapshot');
    } finally {
      setLoading(false);
    }
  }, [deviceId]);
 
  React.useEffect(() => {
    loadThumbnail(value);
  }, [value, loadThumbnail]);
 
  return (
    <div>
      <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <select
          value={value || ''}
          onChange={e => onChange(parseInt(e.target.value))}
          style={{ ...S.formInput, marginTop: 0, flex: 1 }}
        >
          {Array.from({ length: totalChannels }, (_, i) => i + 1).map(n => (
            <option key={n} value={n}>Canal {n}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => loadThumbnail(value)}
          disabled={loading}
          title="Refrescar miniatura"
          style={{
            padding: '8px 10px',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            cursor: loading ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <RefreshCw size={14} className={loading ? 'spin' : ''}/>
        </button>
      </div>
 
      {/* Miniatura 16:9 */}
      <div style={{
        width: '100%',
        aspectRatio: '16/9',
        background: '#000',
        borderRadius: 6,
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {loading && (
          <div style={{ color: '#9ca3af', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={16} className="spin"/> Cargando...
          </div>
        )}
        {!loading && thumbnail && (
          <img
            src={`data:image/jpeg;base64,${thumbnail}`}
            alt={`Canal ${value}`}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        )}
        {!loading && error && (
          <div style={{ color: '#9ca3af', fontSize: 12, textAlign: 'center', padding: 8 }}>
            <XCircle size={20} style={{ marginBottom: 4 }}/>
            <div>{error}</div>
            <div style={{ fontSize: 10, marginTop: 2 }}>Canal {value}</div>
          </div>
        )}
        {/* Badge canal */}
        <div style={{
          position: 'absolute', top: 6, left: 6,
          background: 'rgba(0,0,0,0.7)', color: '#fff',
          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
        }}>
          CH {value}
        </div>
      </div>
 
      {capturedAt && !error && (
        <small style={{ color: '#6b7280', fontSize: 11, marginTop: 4, display: 'block' }}>
          📸 {new Date(capturedAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
        </small>
      )}
    </div>
  );
};
 






















// ============ MODALES (idénticos al original) ============
const Modal = ({ type, device, onClose, onSave }) => {
const [form, setForm] = useState({
  device_id: device?.device_id || '',
  name: device?.name || '',
  description: device?.description || '',
  channels: Number(device?.channels) || 4,
  active_channels: device?.active_channels || '1,2,3,4',
  snapshot_interval: Number(device?.snapshot_interval) || 60,
  is_active: device?.is_active ?? true,
  // Al aprobar un pendiente no hay protocol_type todavia: se usa la pista que
  // manda el backend (por que via se vio llegar al equipo). Sin ella el alta
  // caia siempre en ISUP y un EPCOM quedaba guardado con el protocolo
  // equivocado, sin que nada avisara.
  protocol_type: device?.protocol_type || device?.protocol_hint || 'ISUP',
  default_channel: Number(device?.default_channel) || 1,
  panel_channel_road:   Number(device?.panel_channel_road)   || 1,    // ← Number() aquí
  panel_channel_driver: Number(device?.panel_channel_driver) || 2,    // ← Number() aquí
});
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (type === 'approve') await approvePendingDevice(device.id, form);
      else await updateDevice(device.id, form);
      onSave();
      onClose();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || 'Error'));
    } finally { setSaving(false); }
  };

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <h2 style={S.modalTitle}>{type === 'approve' ? 'Registrar Dispositivo' : 'Editar Dispositivo'}</h2>
        <form onSubmit={handleSubmit}>
          <div style={S.formGroup}>
            <label>Device ID</label>
            <input value={form.device_id} disabled style={{ ...S.formInput, background: '#f3f4f6' }}/>
          </div>
          <div style={S.formGroup}>
            <label>Nombre *</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                   style={S.formInput} required placeholder="Ej: Camión 001"/>
          </div>
          <div style={S.formGroup}>
            <label>Descripción</label>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                   style={S.formInput} placeholder="Opcional"/>
          </div>
          <div style={S.formGroup}>
            <label>Protocolo</label>
            <select value={form.protocol_type} onChange={e => setForm({ ...form, protocol_type: e.target.value })}
                    style={S.formInput}>
              <option value="ISUP">ISUP (DVRs estándar)</option>
              <option value="JT808">JT808 (DashCam G40)</option>
              <option value="N9M">N9M (MDVR EPCOM/XMR)</option>
              <option value="ACS">ACS (terminal de acceso)</option>
            </select>
          </div>
          <div style={S.formRow}>
            <div style={S.formGroup}>
              <label>Total Canales</label>
              <input type="number" value={form.channels}
                     onChange={e => setForm({ ...form, channels: parseInt(e.target.value) || 4 })}
                     style={S.formInput} min="1" max="16"/>
            </div>
            <div style={S.formGroup}>
              <label>Canal Default</label>
              <input type="number" value={form.default_channel}
                     onChange={e => setForm({ ...form, default_channel: parseInt(e.target.value) || 1 })}
                     style={S.formInput} min="1" max="16"/>
            </div>
          </div>
          <div style={S.formGroup}>
            <label>Intervalo legacy (seg)</label>
            <input type="number" value={form.snapshot_interval}
                   onChange={e => setForm({ ...form, snapshot_interval: parseInt(e.target.value) || 60 })}
                   style={S.formInput} min="10"/>
            <small style={{ color: '#6b7280', fontSize: 11 }}>
              Solo se usa si "use_adaptive_interval" está OFF. Configura el adaptativo en /intervals
            </small>
          </div>
          <div style={S.formGroup}>
            <label>Canales Activos</label>
            <input value={form.active_channels} onChange={e => setForm({ ...form, active_channels: e.target.value })}
                   style={S.formInput} placeholder="1,2,3,4"/>
          </div>
          {type === 'edit' && (
            <div style={S.formGroup}>
              <label>
                <input type="checkbox" checked={form.is_active}
                       onChange={e => setForm({ ...form, is_active: e.target.checked })}/> Activo
              </label>
            </div>
          )}




<div style={{ ...S.formGroup, padding: 12, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
  <label style={{ fontWeight: 600, color: '#0369a1', marginBottom: 8, display: 'block' }}>
    📺 Canales para Panel Externo (Samsara-style)
  </label>
  <small style={{ color: '#6b7280', display: 'block', marginBottom: 12 }}>
    Selecciona el canal y mira la vista previa al lado para confirmar.
  </small>
 
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
    <ChannelPreviewSelector
      label="🛣️ Canal Road (frontal)"
      deviceId={device?.device_id}
      totalChannels={form.channels || 4}
      value={form.panel_channel_road}
      onChange={(val) => setForm({ ...form, panel_channel_road: val })}
    />
    <ChannelPreviewSelector
      label="👤 Canal Driver (cabina)"
      deviceId={device?.device_id}
      totalChannels={form.channels || 4}
      value={form.panel_channel_driver}
      onChange={(val) => setForm({ ...form, panel_channel_driver: val })}
    />
  </div>
</div>















          <div style={S.modalActions}>
            <button type="button" onClick={onClose} style={S.btnCancel}>Cancelar</button>
            <button type="submit" style={S.btnSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SnapshotModal = ({ data, onClose }) => {
  const [channel, setChannel] = useState(data.channel || 1);
  const [image, setImage] = useState(null);
  const [capturedAt, setCapturedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSnapshot = useCallback(async (ch) => {
    setLoading(true); setError(null);
    try {
      const res = await getLatestSnapshotBase64(data.device.device_id, ch);
      setImage(res.data.image);
      setCapturedAt(res.data.captured_at);
    } catch (err) {
      setError('No hay snapshot para este canal');
      setImage(null);
    } finally { setLoading(false); }
  }, [data.device.device_id]);

  useEffect(() => { loadSnapshot(channel); }, [channel, loadSnapshot]);

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: '900px' }} onClick={e => e.stopPropagation()}>
        <div style={S.snapshotHeader}>
          <h2 style={S.modalTitle}>{data.device.name}</h2>
          <div style={S.channelSelector}>
            <button onClick={() => channel > 1 && setChannel(channel - 1)} disabled={channel <= 1} style={S.channelBtn}>
              <ChevronLeft size={20}/>
            </button>
            <span style={S.channelLabel}>Canal {channel}</span>
            <button onClick={() => setChannel(channel + 1)} disabled={channel >= (data.device.channels || 4)} style={S.channelBtn}>
              <ChevronRight size={20}/>
            </button>
            <button onClick={() => loadSnapshot(channel)} style={{ ...S.channelBtn, marginLeft: '8px' }}>
              <RefreshCw size={18}/>
            </button>
          </div>
        </div>
        {capturedAt && (
          <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
            Capturado: {new Date(capturedAt).toLocaleString()}
          </p>
        )}
        <div style={S.snapshotContainer}>
          {loading ? (
            <div style={S.snapshotLoading}><RefreshCw size={32}/><p>Cargando...</p></div>
          ) : error ? (
            <div style={S.snapshotError}><XCircle size={48}/><p>{error}</p></div>
          ) : (
            <img src={`data:image/jpeg;base64,${image}`} alt="Snapshot" style={S.snapshotImage}/>
          )}
        </div>
        <div style={S.modalActions}>
          <span style={{ color: '#6b7280', fontSize: '12px' }}>{data.device.protocol_type || 'ISUP'}</span>
          <button onClick={onClose} style={S.btnCancel}>Cerrar</button>
        </div>
      </div>
    </div>
  );
};

const MonitorModal = ({ data, onClose }) => {
  const [channel, setChannel] = useState(data.channel || 1);
  const [image, setImage] = useState(null);
  const [capturedAt, setCapturedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(true);
  const [intervalSec, setIntervalSec] = useState(5);
  const [updateCount, setUpdateCount] = useState(0);
  const intervalRef = useRef(null);

  const loadSnapshot = useCallback(async () => {
    try {
      const res = await getLatestSnapshotBase64(data.device.device_id, channel);
      setImage(res.data.image);
      setCapturedAt(res.data.captured_at);
      setError(null);
      setUpdateCount(prev => prev + 1);
    } catch (err) { setError('Error cargando imagen'); }
    finally { setLoading(false); }
  }, [data.device.device_id, channel]);

  useEffect(() => {
    loadSnapshot();
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (isRunning) intervalRef.current = setInterval(loadSnapshot, intervalSec * 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [loadSnapshot, isRunning, intervalSec]);

  const handleChannelChange = (newChannel) => {
    setChannel(newChannel); setLoading(true); setUpdateCount(0);
  };

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: '1000px' }} onClick={e => e.stopPropagation()}>
        <div style={S.monitorHeader}>
          <div>
            <h2 style={S.modalTitle}>
              <Radio size={20} style={{ color: isRunning ? '#10b981' : '#6b7280', marginRight: '8px' }}/>
              {data.device.name}
            </h2>
            <p style={{ color: '#6b7280', margin: '4px 0 0 0', fontSize: '13px' }}>
              {isRunning ? `🔴 En vivo • Cada ${intervalSec}s` : '⏸️ Pausado'}
              {updateCount > 0 && ` • ${updateCount} actualizaciones`}
            </p>
          </div>
          <button onClick={() => setIsRunning(!isRunning)} style={{
            ...S.monitorBtn,
            background: isRunning ? '#fee2e2' : '#dcfce7',
            color: isRunning ? '#dc2626' : '#16a34a'
          }}>
            {isRunning ? <><Square size={16}/> Pausar</> : <><Play size={16}/> Reanudar</>}
          </button>
        </div>
        <div style={S.monitorSettings}>
          <div style={S.channelSelector}>
            <button onClick={() => channel > 1 && handleChannelChange(channel - 1)}
                    disabled={channel <= 1} style={S.channelBtn}>
              <ChevronLeft size={20}/>
            </button>
            <span style={S.channelLabel}>Canal {channel}</span>
            <button onClick={() => handleChannelChange(channel + 1)}
                    disabled={channel >= (data.device.channels || 4)} style={S.channelBtn}>
              <ChevronRight size={20}/>
            </button>
          </div>
          <div style={S.intervalSelector}>
            <label style={{ fontSize: '13px', color: '#6b7280', marginRight: '8px' }}>Intervalo:</label>
            <select value={intervalSec} onChange={e => setIntervalSec(parseInt(e.target.value))} style={S.intervalSelect}>
              <option value={3}>3 seg</option>
              <option value={5}>5 seg</option>
              <option value={10}>10 seg</option>
              <option value={15}>15 seg</option>
              <option value={30}>30 seg</option>
              <option value={60}>1 min</option>
            </select>
          </div>
          {capturedAt && (
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              Última: {new Date(capturedAt).toLocaleTimeString()}
            </span>
          )}
        </div>
        <div style={S.monitorContainer}>
          {loading && !image ? (
            <div style={S.snapshotLoading}><RefreshCw size={32}/><p>Cargando...</p></div>
          ) : error && !image ? (
            <div style={S.snapshotError}><XCircle size={48}/><p>{error}</p></div>
          ) : (
            <div style={S.monitorImageWrapper}>
              <img src={`data:image/jpeg;base64,${image}`} alt="Monitor" style={S.monitorImage}/>
              {isRunning && (
                <div style={S.liveIndicator}>
                  <span style={S.liveDot}></span> LIVE
                </div>
              )}
            </div>
          )}
        </div>
        <div style={S.modalActions}>
          <span style={{ color: '#6b7280', fontSize: '12px' }}>
            {data.device.protocol_type || 'ISUP'} • {data.device.device_id}
          </span>
          <button onClick={onClose} style={S.btnCancel}>Cerrar</button>
        </div>
      </div>
    </div>
  );
};

// ============ Estilos ============
// Modal para ligar un equipo a su unidad. La lista sale de DB_PAQ (unidades que
// todavia no tienen equipo), asi que aqui no se escribe un economico a mano: se
// elige de las que existen, que es lo que evita los numeros inventados que luego
// no cruzan con nada.
const LigarModal = ({ device, unidades, error, onClose, onConfirm }) => {
  const [sel, setSel] = useState('');
  const [busca, setBusca] = useState('');
  const lista = (unidades || []).filter(u =>
    !busca.trim() || (u.nombre || '').toLowerCase().includes(busca.toLowerCase()));

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalBox} onClick={e => e.stopPropagation()}>
        <h3 style={S.modalTitle}><LinkIcon size={18}/> Ligar a una unidad</h3>
        <p style={S.modalSub}>
          <b>{device.name}</b> <span style={S.modalMono}>{device.device_id}</span>
        </p>

        {error && <div style={S.modalError}>{error}</div>}

        {unidades === null && <div style={S.modalInfo}>Consultando DB_PAQ…</div>}

        {unidades !== null && unidades.length === 0 && !error && (
          <div style={S.modalInfo}>
            No hay unidades libres en DB_PAQ. Si la unidad ya tiene otro equipo
            ligado, primero hay que soltarla allá.
          </div>
        )}

        {unidades !== null && unidades.length > 0 && (
          <>
            <input style={S.modalInput} placeholder="Buscar unidad…"
                   value={busca} onChange={e => setBusca(e.target.value)} autoFocus/>
            <select style={S.modalSelect} size={8} value={sel}
                    onChange={e => setSel(e.target.value)}>
              {lista.map(u => (
                <option key={u.id} value={u.id}>{u.nombre}</option>
              ))}
            </select>
            <p style={S.modalNota}>
              Se escribe en los dos lados: DB_PAQ (de donde Grafana saca si el MDVR
              está configurado) y el snap (que es lo que usa el portal y el cruce
              con el consumo de Samsara).
            </p>
          </>
        )}

        <div style={S.modalBtns}>
          <button style={S.modalCancel} onClick={onClose}>Cancelar</button>
          <button style={{ ...S.modalOk, opacity: sel ? 1 : 0.5 }}
                  disabled={!sel} onClick={() => onConfirm(sel)}>Ligar</button>
        </div>
      </div>
    </div>
  );
};

const S = {
  unidadTag: { display: 'inline-block', marginTop: 3, fontSize: 11, fontWeight: 700,
               color: '#1e40af', background: '#dbeafe', padding: '1px 7px', borderRadius: 4 },
  ligarBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 3,
              fontSize: 11, color: '#92400e', background: '#fef3c7', border: 'none',
              padding: '2px 7px', borderRadius: 4, cursor: 'pointer' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { background: 'white', borderRadius: 10, padding: 20, width: 460, maxWidth: '92vw',
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)' },
  modalTitle: { margin: 0, fontSize: 17, display: 'flex', alignItems: 'center', gap: 8 },
  modalSub: { margin: '6px 0 12px', fontSize: 13, color: '#374151' },
  modalMono: { fontFamily: 'monospace', fontSize: 11, color: '#9ca3af', marginLeft: 6 },
  modalError: { background: '#fee2e2', color: '#991b1b', padding: '8px 10px',
                borderRadius: 6, fontSize: 13, marginBottom: 10 },
  modalInfo: { background: '#f3f4f6', color: '#374151', padding: '10px 12px',
               borderRadius: 6, fontSize: 13, marginBottom: 10 },
  modalInput: { width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb',
                borderRadius: 6, fontSize: 13, marginBottom: 8, boxSizing: 'border-box' },
  modalSelect: { width: '100%', padding: 6, border: '1px solid #e5e7eb', borderRadius: 6,
                 fontSize: 13, boxSizing: 'border-box' },
  modalNota: { fontSize: 11, color: '#6b7280', margin: '8px 0 0' },
  modalBtns: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 },
  modalCancel: { padding: '8px 14px', background: 'white', border: '1px solid #e5e7eb',
                 borderRadius: 7, cursor: 'pointer', fontSize: 13 },
  modalOk: { padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none',
             borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  container: { padding: '24px', maxWidth: '1500px', margin: '0 auto' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '20px' },
  kpiCard: { background: 'white', borderRadius: '8px', padding: '14px', display: 'flex', alignItems: 'center', gap: '12px',
             boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderLeft: '4px solid', transition: 'transform 0.1s' },
  kpiIcon: { width: '40px', height: '40px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  kpiValue: { fontSize: '22px', fontWeight: 'bold', color: '#1f2937', lineHeight: 1 },
  kpiLabel: { color: '#6b7280', fontSize: '12px', marginTop: '4px' },

  tabs: { display: 'flex', gap: '8px', marginBottom: '12px' },
  tab: { padding: '10px 20px', border: 'none', background: '#e5e7eb', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' },
  tabActive: { padding: '10px 20px', border: 'none', background: '#3b82f6', color: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' },

  filterBar: { display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 280px', background: 'white',
               padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '14px' },
  select: { padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', background: 'white', fontSize: '14px', cursor: 'pointer' },
  clearBtn: { display: 'flex', gap: '4px', alignItems: 'center', padding: '8px 12px', background: '#fee2e2',
              color: '#991b1b', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },

  tableContainer: { background: 'white', borderRadius: '12px', overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '12px 14px', background: '#f9fafb', fontWeight: '600', color: '#374151',
        borderBottom: '1px solid #e5e7eb', fontSize: '11px', textTransform: 'uppercase', cursor: 'pointer', userSelect: 'none' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '12px 14px', fontSize: '14px' },
  online: { display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#10b981', fontWeight: '500', fontSize: '13px' },
  offline: { display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#9ca3af', fontSize: '13px' },
  code: { background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontFamily: 'monospace' },
  protocolBadge: { padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' },
  badge: { display: 'inline-flex', alignItems: 'center', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500 },
  alertBadge: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px',
                borderRadius: '4px', background: '#fee2e2', color: '#991b1b', fontSize: '12px', fontWeight: 600 },

  actions: { display: 'flex', gap: '4px' },
  actionBtn: { display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 8px',
               border: '1px solid #e5e7eb', borderRadius: '6px', background: 'white', cursor: 'pointer', color: '#374151' },
  miniBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', fontSize: 11,
             border: '1px solid #d1d5db', borderRadius: 6, background: 'white', cursor: 'pointer', color: '#374151' },
  miniBtnWarn: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', fontSize: 11,
                 border: '1px solid #f59e0b', borderRadius: 6, background: '#fffbeb', cursor: 'pointer', color: '#b45309', fontWeight: 600 },
  empty: { padding: '40px', textAlign: 'center', color: '#9ca3af' },
  loading: { padding: '60px', textAlign: 'center', color: '#6b7280' },

  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '500px',
           margin: '20px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: '20px', fontWeight: 'bold', marginBottom: '4px', display: 'flex', alignItems: 'center' },
  formGroup: { marginBottom: '16px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formInput: { width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', marginTop: '4px', boxSizing: 'border-box' },
  modalActions: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '24px' },
  btnCancel: { padding: '10px 20px', border: '1px solid #e5e7eb', borderRadius: '6px', background: 'white', cursor: 'pointer' },
  btnSave: { padding: '10px 20px', border: 'none', borderRadius: '6px', background: '#3b82f6', color: 'white', cursor: 'pointer', fontWeight: '500' },

  snapshotHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  channelSelector: { display: 'flex', alignItems: 'center', gap: '4px' },
  channelBtn: { padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px', background: 'white', cursor: 'pointer', display: 'flex' },
  channelLabel: { padding: '0 12px', fontWeight: '600', minWidth: '80px', textAlign: 'center' },
  snapshotContainer: { minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                       background: '#f9fafb', borderRadius: '8px', overflow: 'hidden' },
  snapshotImage: { width: '100%', maxHeight: '500px', objectFit: 'contain' },
  snapshotLoading: { textAlign: 'center', color: '#6b7280' },
  snapshotError: { textAlign: 'center', color: '#ef4444' },

  monitorHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  monitorBtn: { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', border: 'none',
                borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '14px' },
  monitorSettings: { display: 'flex', alignItems: 'center', gap: '24px', padding: '12px 16px',
                     background: '#f9fafb', borderRadius: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  intervalSelector: { display: 'flex', alignItems: 'center' },
  intervalSelect: { padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', background: 'white', cursor: 'pointer', fontSize: '13px' },
  monitorContainer: { minHeight: '450px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: '#000', borderRadius: '8px', overflow: 'hidden', position: 'relative' },
  monitorImageWrapper: { position: 'relative', width: '100%', height: '100%' },
  monitorImage: { width: '100%', maxHeight: '550px', objectFit: 'contain', display: 'block' },
  liveIndicator: { position: 'absolute', top: '12px', right: '12px', background: 'rgba(0,0,0,0.7)',
                   color: '#ef4444', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold',
                   display: 'flex', alignItems: 'center', gap: '6px' },
  liveDot: { width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', animation: 'pulse 1.5s infinite' }
};

// CSS animations
if (typeof document !== 'undefined' && !document.getElementById('dashboard-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'dashboard-styles';
   styleSheet.textContent = `
   @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
   @keyframes spin { to { transform: rotate(360deg); } }
   .spin { animation: spin 1s linear infinite; }
 `;
  document.head.appendChild(styleSheet);
}

export default Dashboard;